import type { IEnv as IEnvironment } from '../interface/env';

type IEnvironmentStorage = Record<string, unknown> | undefined;

const BOOLEAN_PHRASES_ENABLED = Object.freeze(new Set(['true', 'yes', 'on', 'enable', 'enabled']));
const BOOLEAN_PHRASES_DISABLED = Object.freeze(
    new Set(['false', 'no', 'off', 'disable', 'disabled', 'null', 'undefined']),
);

const VALUE_PARSERS = {
    string: (value: unknown) => String(value),
    number: (value: unknown) => Number(value),
    bigint: (value: unknown) => (typeof value === 'bigint' ? value : BigInt(Number(value))),
    boolean: (value: unknown) => {
        let returnValue = false;

        if (typeof value === 'boolean') {
            returnValue = value;
        } else if (typeof value === 'string') {
            /* Support string typed boolean values: on,off,etc.. */
            const stringValue = value.toLowerCase();
            if (BOOLEAN_PHRASES_ENABLED.has(stringValue)) {
                returnValue = true;
            } else if (BOOLEAN_PHRASES_DISABLED.has(stringValue)) {
                returnValue = false;
            } else if (!Number.isNaN(Number.parseInt(value, 10))) {
                returnValue = Boolean(Number.parseInt(value, 10));
            } else {
                returnValue = Boolean(value);
            }
        } else {
            returnValue = Boolean(value);
        }

        return returnValue;
    },
    symbol: (value: unknown) => Symbol.for(String(value)),
    /* eslint-disable-next-line unicorn/no-useless-undefined */
    undefined: (value: unknown) => undefined,
    object: (value: unknown) => (typeof value === 'object' ? value : {}),
    /* eslint-disable-next-line unicorn/no-useless-undefined */
    function: (value: unknown) => undefined,
} as const;

function _queryStorage<T>(
    environmentStorage: IEnvironmentStorage,
    environmentName: string,
): T | null {
    const records = environmentStorage ?? {};
    const value = records[environmentName] ?? null;
    return value as T | null;
}

function _getValue<T>(
    environmentStorage: IEnvironmentStorage[],
    environmentName: string,
): T | null {
    for (const storage of environmentStorage) {
        const value = _queryStorage<T>(storage, environmentName);

        if (value !== null) {
            return value;
        }
    }

    return null;
}

function _getValueWithDefault<T>(
    environmentStorage: IEnvironmentStorage[],
    environmentName: string,
    defaultValue: T,
): T {
    const value = _getValue<T>(environmentStorage, environmentName) ?? defaultValue;
    const parserType = typeof defaultValue;
    const parser = VALUE_PARSERS[parserType];
    /* We use typeof defaultValue, which is T & that matches the return value */
    return parser(value) as unknown as T;
}

function _getNumber(
    environmentStorage: IEnvironmentStorage[],
    name: string,
    defaultValue = -1,
): number {
    const value = _getValue<unknown | undefined>(environmentStorage, name) ?? defaultValue;
    return VALUE_PARSERS.number(value);
}

function _getBoolean(
    environmentStorage: IEnvironmentStorage[],
    name: string,
    defaultValue = false,
): boolean {
    const readValue = _getValue<unknown | undefined>(environmentStorage, name) ?? defaultValue;
    return VALUE_PARSERS.boolean(readValue);
}

function _getString(
    environmentStorage: IEnvironmentStorage[],
    name: string,
    defaultValue = '',
): string {
    const value = _getValue<unknown | undefined>(environmentStorage, name) ?? defaultValue;
    return VALUE_PARSERS.string(value);
}

function _isRecord(object: unknown): object is Record<string, unknown> {
    if (object === undefined || object === null || typeof object !== 'object') {
        return false;
    }

    return true;
}

function environment(): IEnvironment {
    const isRunningOnNodeJS = typeof window === 'undefined';

    /* eslint-disable-next-line no-restricted-globals */
    const globalObject: Record<string, unknown> = isRunningOnNodeJS ? globalThis : window;

    const candidates = [
        globalObject['env'],
        globalObject['ENV'],
        /* Expose the process variables on the window */
        process.env,
    ];

    const environmentStorage: IEnvironmentStorage[] = candidates.map(item =>
        _isRecord(item) ? item : {},
    );
    const mergedValues = environmentStorage.reduce(
        (previous, current) => ({ ...previous, ...current }),
        {},
    );

    const module: IEnvironment = {
        getValue: (environmentName: string) => _getValue(environmentStorage, environmentName),
        getValueWithDefault: <T>(environmentName: string, defaultValue: T) =>
            _getValueWithDefault(environmentStorage, environmentName, defaultValue),
        getNumber: (name: string, defaultValue?: number) =>
            _getNumber(environmentStorage, name, defaultValue ?? -1),
        getBoolean: (name: string, defaultValue?: boolean) =>
            _getBoolean(environmentStorage, name, defaultValue ?? false),
        getString: (name: string, defaultValue?: string) =>
            _getString(environmentStorage, name, defaultValue ?? ''),
        allValues: () => mergedValues ?? {},
    };

    return module;
}

export default environment;
